// The Triage cases read for "Decide which commands run without approval": the
// last 30 days' findings that met a standard, as Triage lists them, and the
// tool calls their citations point at, grouped by the rule that covers them.
//
// Bounded: the newest 50 cases, and their first 256 cited message parts. Per
// session (trace), the cited messages' tool parts are looked up first, so the
// content read gets each part's current identity: a citation can pin an older
// capture of its message, and the content read refuses a whole batch when one
// identity is stale. The content reads take one session each and go through
// the shared trace read gate. The reads run once, when the step's card is
// shown.
import { rangeToTimeRange, runQueryRows } from "../../../lib/analyticsClient.js";
import { MAX_CONTENT_REFERENCES, conversationPartToBlock, readConversationContent } from "../../../lib/conversationContent.js";
import { readFleetPlaces } from "../../../lib/fleetFindings.js";
import { withTraceReadSlot } from "../../../lib/traceReadGate.js";
import { uuidToEndpointId } from "../../../lib/inventoryRows.js";
import { reviewsWindow } from "../../../lib/scopeReviews.js";
import { caseGroupRows } from "./commandGroups.js";

export const CASE_CAP = 50;
// A session can begin before the review window does, so its messages are read
// from this long before the window's start.
const SESSION_LEAD_MS = 7 * 24 * 60 * 60 * 1000;
const NIL_ID = "00000000-0000-0000-0000-000000000000";
const F = (name) => ({ stepAlias: "f", field: `DIMENSION_FIELD_CONVERSATION_REVIEW_FINDING_${name}` });
const P = (name) => ({ stepAlias: "mp", field: `DIMENSION_FIELD_CONVERSATION_MESSAGE_PART_${name}` });
const TOOL_KINDS = ["tool_use", "tool_result"];
// The most parts one message is read with; the session view reads as many.
const PARTS_PER_MESSAGE = 60;

/// The analytics read of the cases' citation references.
export function citationSpec(ids, { fromMs, toMs }) {
    return {
        root: { alias: "f", entity: "ENTITY_TYPE_CONVERSATION_REVIEW_FINDING" },
        timeRange: rangeToTimeRange({ from: new Date(fromMs).toISOString(), to: new Date(toMs).toISOString() }),
        filter: { pred: { field: F("ID"), op: "OPERATOR_IN", value: ids } },
        output: {
            dimensions: [
                ["ID", "finding_id"], ["CITATION_SOURCE_EVENT_IDS", "source_event_ids"],
                ["CITATION_MESSAGE_IDS", "message_ids"], ["CITATION_PART_ORDINALS", "part_ordinals"],
            ].map(([name, outputAlias]) => ({ field: F(name), outputAlias })),
        },
        limit: ids.length,
    };
}

/// The current tool parts of one session's cited messages.
export function toolPartSpec(session, messageIds, timeRange) {
    return {
        root: { alias: "mp", entity: "ENTITY_TYPE_CONVERSATION_MESSAGE_PART" },
        timeRange,
        // The TRACE_ID predicate is the planner's admission requirement.
        filter: { andExpr: { children: [
            { pred: { field: P("TRACE_ID"), op: "OPERATOR_EQ", value: session } },
            { pred: { field: P("MESSAGE_ID"), op: "OPERATOR_IN", value: messageIds } },
            { pred: { field: P("KIND"), op: "OPERATOR_IN", value: TOOL_KINDS } },
        ] } },
        output: {
            dimensions: [["MESSAGE_ID", "message_id"], ["ORDINAL", "ordinal"], ["SOURCE_EVENT_ID", "source_id"]]
                .map(([name, outputAlias]) => ({ field: P(name), outputAlias })),
        },
        limit: messageIds.length * PARTS_PER_MESSAGE,
    };
}

/// Each finding's cited parts as `{ findingId, sourceEventId, messageId,
/// ordinal }`, in citation order, without repeats or missing ids.
export function citedParts(rows) {
    const seen = new Set();
    const out = [];
    for (const row of rows ?? []) {
        const events = Array.isArray(row.source_event_ids) ? row.source_event_ids : [];
        const messages = Array.isArray(row.message_ids) ? row.message_ids : [];
        const ordinals = Array.isArray(row.part_ordinals) ? row.part_ordinals : [];
        for (let i = 0; i < messages.length; i++) {
            const messageId = String(messages[i] ?? "").toLowerCase();
            const sourceEventId = String(events[i] ?? "").toLowerCase();
            if (!messageId || messageId === NIL_ID || !sourceEventId || sourceEventId === NIL_ID) continue;
            const ordinal = Number(ordinals[i] ?? 0);
            const key = `${row.finding_id}|${messageId}|${ordinal}`;
            if (seen.has(key)) continue;
            seen.add(key);
            out.push({ findingId: String(row.finding_id), sourceEventId, messageId, ordinal });
        }
    }
    return out;
}

/**
 * Rows for the Triage cases step (see commandGroups.js). The readers are
 * parameters so tests run without the network.
 */
export async function readTriageCases({
    readFindings = async () => (await readFleetPlaces(null)).findings,
    selectedCase = false,
    includeEvidence = false,
    reviewWindow = null,
    query = runQueryRows,
    readContent = (request) => withTraceReadSlot(null, () => readConversationContent(request)),
} = {}) {
    const findings = await readFindings();
    const cases = findings.filter((f) => selectedCase || f.met?.length).sort((a, b) => (b.at ?? 0) - (a.at ?? 0)).slice(0, CASE_CAP);
    if (!cases.length) return [];
    const window = reviewWindow || reviewsWindow(null);
    const sessionOf = new Map(cases.map((c) => [c.id, c.session]));
    const parts = citedParts(await query(citationSpec(cases.map((c) => c.id), window)))
        .filter((p) => sessionOf.get(p.findingId))
        .slice(0, MAX_CONTENT_REFERENCES);
    const bySession = new Map();
    for (const p of parts) {
        const session = sessionOf.get(p.findingId);
        bySession.set(session, [...(bySession.get(session) ?? []), p]);
    }
    const timeRange = rangeToTimeRange({ from: new Date(window.fromMs - SESSION_LEAD_MS).toISOString(), to: new Date(window.toMs).toISOString() });
    const partKey = (p) => `${p.messageId}|${p.ordinal}`;
    const calls = new Map();
    await Promise.all([...bySession].map(async ([session, sessionParts]) => {
        const messageIds = [...new Set(sessionParts.map((p) => p.messageId))];
        const current = new Map();
        for (const row of await withTraceReadSlot(null, () => query(toolPartSpec(session, messageIds, timeRange))) ?? []) {
            const part = { messageId: String(row.message_id ?? "").toLowerCase(), ordinal: Number(row.ordinal ?? 0), sourceEventId: String(row.source_id ?? "").toLowerCase() };
            if (part.messageId && part.sourceEventId && part.sourceEventId !== NIL_ID) current.set(partKey(part), part);
        }
        // Only cited parts that are tool parts are read, each once: two cases
        // can cite the same part.
        const unique = [...new Set(sessionParts.map(partKey))].map((key) => current.get(key)).filter(Boolean);
        if (!unique.length) return;
        const contents = await readContent({
            traceId: uuidToEndpointId(session),
            timeRange,
            references: unique.map((p) => ({ messagePart: { sourceEventId: uuidToEndpointId(p.sourceEventId), messageId: uuidToEndpointId(p.messageId), ordinal: p.ordinal } })),
        });
        const blocks = new Map();
        contents.forEach((content, i) => {
            const block = content?.messagePart ? conversationPartToBlock(content.messagePart) : null;
            if (block?.type === "tool_use" || block?.type === "tool_result") blocks.set(partKey(unique[i]), block);
        });
        for (const p of sessionParts) {
            const block = blocks.get(partKey(p));
            if (block) calls.set(p.findingId, [...(calls.get(p.findingId) ?? []), block]);
        }
    }));
    const groups = caseGroupRows(cases.map((c) => ({ id: c.id, title: c.title || "Untitled case" })), calls);
    return includeEvidence ? { groups, findings: cases.map((c) => ({ id: c.id, title: c.title, session: c.session, citedToolParts: calls.get(c.id) || [] })), citedPartLimit: MAX_CONTENT_REFERENCES } : groups;
}
